import { z } from "zod";
import { isoDateTimeSchema } from "./time";
import { galaxyRoleSchema } from "./galaxy";

export const moodSchema = z.enum([
  "calm",
  "warm",
  "joyful",
  "excited",
  "moved",
  "nostalgic",
  "grateful",
  "peaceful",
  "melancholy"
]);
export type Mood = z.infer<typeof moodSchema>;

export const memorySpaceSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(48),
  createdBy: z.string().uuid(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
export type MemorySpace = z.infer<typeof memorySpaceSchema>;

export const membershipSchema = z.object({
  id: z.string().uuid(),
  spaceId: z.string().uuid(),
  userId: z.string().uuid(),
  role: galaxyRoleSchema,
  joinedAt: isoDateTimeSchema
});
export type Membership = z.infer<typeof membershipSchema>;

export const pointSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1)
});

export const doodleStrokeSchema = z.object({
  id: z.string().min(1),
  authorId: z.string().min(1),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
  width: z.number().positive().max(24),
  points: z.array(pointSchema).min(2),
  createdAt: isoDateTimeSchema
});
export type DoodleStroke = z.infer<typeof doodleStrokeSchema>;

export const mediaAssetSchema = z.object({
  id: z.string().min(1),
  memoryId: z.string().min(1),
  ownerId: z.string().min(1),
  fileName: z.string().min(1),
  mimeType: z.string().startsWith("image/"),
  size: z.number().int().nonnegative(),
  createdAt: isoDateTimeSchema,
  // Existing local demo records may predate per-photo time and interaction
  // settings. The cloud migration fills both values for every persisted row.
  capturedAt: isoDateTimeSchema.optional(),
  allowComments: z.boolean().default(true),
  allowDoodles: z.boolean().default(true)
});
export type MediaAsset = z.infer<typeof mediaAssetSchema>;

export const memorySchema = z.object({
  id: z.string().min(1),
  spaceId: z.string().min(1),
  title: z.string().trim().min(1).max(80),
  body: z.string().trim().max(4000),
  occurredAt: isoDateTimeSchema,
  mood: moodSchema.nullable(),
  // A memory can keep its writing, date, mood and doodles after its last
  // photo is removed. New memory creation still requires media in the UI.
  mediaIds: z.array(z.string().min(1)),
  doodleStrokes: z.array(doodleStrokeSchema),
  createdBy: z.string().min(1),
  updatedBy: z.string().min(1),
  version: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  deletedAt: isoDateTimeSchema.nullable()
});
export type Memory = z.infer<typeof memorySchema>;

export const createMemoryInputSchema = memorySchema.pick({
  title: true,
  body: true,
  occurredAt: true,
  mood: true,
  doodleStrokes: true
});
export type CreateMemoryInput = z.infer<typeof createMemoryInputSchema>;

export interface MemoryWithMedia {
  memory: Memory;
  media: Array<MediaAsset & { url: string }>;
}
